import type React from 'react'
import { Box, Button, Typography, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useEffect, useEffectEvent } from 'react'
import { FFModal } from '~/shared/ui'
import { useDeleteTasks } from '~/entities/task'
import CreateFromTemplateForm from './CreateFromTemplateForm'
import EditOrCustomCreateActions from './EditOrCustomCreateActions'
import CreateFromTemplateActions from './CreateFromTemplateActions'
import EditOrCustomCreateForm from './EditOrCustomCreateForm'
import CreateModeSelector from './CreateModeSelector'
import { useCarePlanModal, CREATE_MODES } from './useCarePlanModal'
import type {
  CarePlanCreateResponse,
  CarePlanRetrieveResponse,
  CarePlanV2ListResponse,
} from '@fireflyhealth/api'
import type { PatientUser } from '~/shared/types'
import type { RootState } from '~/app/store/configureStore'

interface ICarePlansModalProps {
  handleModalAction: (carePlan: CarePlanCreateResponse | CarePlanRetrieveResponse) => void
  carePlan: CarePlanV2ListResponse[number] | null
  me: RootState['me']
  open: boolean
  closeModal: () => void
  patient: PatientUser
  existingCarePlanTypeIds?: { [key: number]: boolean }
}

const CarePlansModal = (props: ICarePlansModalProps) => {
  const {
    resetToDefaultState,
    loadCarePlan,
    resetTemplateWorkflow,
    setCreateMode,
    saving,
    setSaving,
    setSelectedTemplate,
    createMode,
    selectedTemplate,
    title,
    notes,
    setTitle,
    setNotes,
    tasks,
    setTasks,
    selectTemplate,
    previewData,
    previewDataLoading,
    previewDataError,
    carePlanTemplates,
    loadCarePlanTemplates,
    carePlanTemplatesLoading,
    readyToSaveFromTemplateId,
    allTemplateTasksComplete,
    setAllTemplateTasksComplete,
    internalOnly,
    setInternalOnly,
  } = useCarePlanModal(props.me)
  const { mutateAsync: deleteTasks } = useDeleteTasks()
  const creatingFromTemplate = !props.carePlan && createMode === CREATE_MODES.TEMPLATE
  const modalTitle = props.carePlan ? 'Edit Care Plan' : 'New Care Plan'

  // Seeds the form when the modal opens. `useCarePlanModal` rebuilds both helpers every render,
  // so they are reached through an event: re-running on them would discard what the user typed.
  const seedForm = useEffectEvent(() => {
    resetToDefaultState()
    if (props.carePlan) {
      loadCarePlan(props.carePlan)
    }
  })
  useEffect(() => {
    if (props.open) {
      seedForm()
    }
  }, [props.open, props.carePlan])

  if (!props.open) return <></>

  const handleCreateModeSelection = (mode: string) => {
    if (mode !== CREATE_MODES.TEMPLATE) {
      resetTemplateWorkflow()
    }
    setCreateMode(mode)
  }

  const closeModal = () => {
    resetToDefaultState()
    props.closeModal()
  }

  const dispatchRemoveTasks = async (tasks: Array<{ id: number }>): Promise<void> => {
    await deleteTasks(tasks)
  }

  const formContents = (): React.JSX.Element => {
    if (creatingFromTemplate && props.existingCarePlanTypeIds) {
      return (
        <CreateFromTemplateForm
          patient={props.patient}
          me={props.me}
          tasks={tasks}
          setTasks={setTasks}
          saving={saving}
          title={title}
          setTitle={setTitle}
          setNotes={setNotes}
          notes={notes}
          selectTemplate={selectTemplate}
          selectedTemplate={selectedTemplate}
          previewData={previewData}
          previewDataLoading={previewDataLoading}
          previewDataError={previewDataError}
          carePlanTemplates={carePlanTemplates}
          loadCarePlanTemplates={loadCarePlanTemplates}
          carePlanTemplatesLoading={carePlanTemplatesLoading}
          setAllTemplateTasksComplete={setAllTemplateTasksComplete}
          existingCarePlanTypeIds={props.existingCarePlanTypeIds}
          internalOnly={internalOnly}
          setInternalOnly={setInternalOnly}
        />
      )
    }

    return (
      <EditOrCustomCreateForm
        patient={props.patient}
        me={props.me}
        tasks={tasks}
        setTasks={setTasks}
        saving={saving}
        title={title}
        notes={notes}
        setTitle={setTitle}
        setNotes={setNotes}
        internalOnly={internalOnly}
        setInternalOnly={setInternalOnly}
      />
    )
  }

  return (
    <FFModal
      fixedHeight={true}
      open={true}
      keepMounted={false}
      header={
        <>
          {selectedTemplate && (
            <Box sx={{ m: 1, mb: -1 }}>
              <Button onClick={() => setSelectedTemplate(null)}>
                <ArrowBackIcon />
                Back to template selection
              </Button>
            </Box>
          )}
          <Box sx={{ m: 2 }}>
            <Typography variant="h5">{modalTitle}</Typography>
          </Box>
        </>
      }
      footer={
        creatingFromTemplate ? (
          <CreateFromTemplateActions
            closeModal={closeModal}
            patient={props.patient}
            handleModalAction={props.handleModalAction}
            tasks={tasks}
            readyToSaveFromTemplateId={readyToSaveFromTemplateId}
            saving={saving}
            setSaving={setSaving}
            allTemplateTasksComplete={allTemplateTasksComplete}
            title={title}
            notes={notes}
            previewData={previewData}
            internalOnly={internalOnly}
          />
        ) : (
          <EditOrCustomCreateActions
            carePlan={props.carePlan}
            patient={props.patient}
            closeModal={closeModal}
            handleModalAction={props.handleModalAction}
            dispatchRemoveTasks={dispatchRemoveTasks}
            me={props.me}
            tasks={tasks}
            saving={saving}
            setSaving={setSaving}
            title={title}
            notes={notes}
            internalOnly={internalOnly}
          />
        )
      }
    >
      <Box sx={{ m: 2 }}>
        <IconButton
          disabled={saving}
          sx={{
            position: 'absolute',
            zIndex: 100,
            top: theme => theme.spacing(1),
            right: theme => theme.spacing(1),
          }}
          onClick={props.closeModal}
        >
          <CloseIcon />
        </IconButton>
        <CreateModeSelector
          carePlan={props.carePlan}
          createMode={createMode}
          selectedTemplate={selectedTemplate}
          me={props.me}
          handleCreateModeSelection={handleCreateModeSelection}
        />
        <Box sx={{ mt: 2 }}>{formContents()}</Box>
      </Box>
    </FFModal>
  )
}

export default CarePlansModal
